// cacheBehavior → { staleTime, gcTime }. См. docs/data-layer.md §9.

export type CacheBehavior = 'fresh' | 'default' | 'persistent' | 'no-cache'

export type CacheSettings = {
  staleTime: number
  gcTime: number
}

const SECOND_MS = 1_000
const MINUTE_MS = 60_000

/**
 * `staleTime` отвечает на вопрос «перезапрашивать ли», `gcTime` — «есть ли что показать, пока
 * запрос идёт». Это две разные вещи, и путать их дорого: `gcTime: 0` выкидывает данные в тот
 * момент, когда экран уходит, поэтому возврат на него всегда начинается со скелетона, даже если
 * ответ пришёл секунду назад.
 *
 * Поэтому «данные обязаны быть свежими» — это `fresh` (перезапрос при каждом входе, но кэш
 * показываем мгновенно и обновляем фоном), а не `no-cache`.
 *
 * `gcTime` заметно больше времени жизни экрана: кэш всё равно умирает вместе с запуском
 * мини-аппы, а держать его полчаса стоит десятков килобайт — и избавляет от скелетона там,
 * где человек просто вернулся на вкладку, с которой ушёл.
 *
 * ПОЧЕМУ У `fresh` НЕ НОЛЬ. `staleTime: 0` означает «запрос протух в момент, когда пришёл
 * ответ», то есть перезапрос на КАЖДОМ монтировании. Замер на живом приложении: переход
 * «Узы → Главная» отправлял шесть запросов подряд, потому что `link.list`, `link.invites` и
 * `link.limits` считались протухшими оба раза. В домашней сети это незаметно, а у нас
 * аудитория сидит через VPN, где каждый такой запрос — отдельный round-trip, и приложение
 * всё время занято обновлением того, что обновилось секунду назад.
 *
 * Полминуты решают это без потери смысла: человек, вернувшийся на экран через минуту,
 * по-прежнему получает перезапрос, а тот, кто скачет между вкладками, — нет. Свои же
 * изменения видны мгновенно в любом случае: мутации инвалидируют кэш явно, минуя `staleTime`.
 */
const CACHE_PRESETS: Record<CacheBehavior, CacheSettings> = {
  fresh: { staleTime: 30 * SECOND_MS, gcTime: 30 * MINUTE_MS },
  default: { staleTime: 5 * MINUTE_MS, gcTime: 30 * MINUTE_MS },
  persistent: { staleTime: Infinity, gcTime: Infinity },
  'no-cache': { staleTime: 0, gcTime: 0 },
}

export function getCacheSettings(behavior: CacheBehavior = 'default'): CacheSettings {
  return CACHE_PRESETS[behavior]
}
